import { AnimatePresence } from 'framer-motion';
import { up } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';

import { PropsWithRequiredChildren } from '@common/types';
import {
  useFloatingPlayer,
  useNotifyUserIsOnlineEffect,
  useSurveyChannel,
  useTimelineChannel,
} from '@hooks';
import { FloatingPlayer, Modal, TawkTo, WhatsAppButton } from '@components';
import { IntegrationLocation } from '@common/types/api';
import { selectIsFloatingPlayerVisible } from '@hooks/useFloatingPlayer/selectors';

import { AppShell, Header } from '../../../layouts';
// import { Sidebar } from '../../../layouts'; // Sidebar novo (só ícones)
import OldSidebar from './Navigation/Sidebar'; // Sidebar antigo (expande com texto no hover)
import SurveyModal from './SurveyModal';
import SurveyNotification from './SurveyNotification';
import GlobalModals from './GlobalModals';
import CertificateModal from './CertificateModal';
import DropdownLang from './Navigation/DropdownLang';
import DropdownMenu from './Navigation/DropdownMenu';
import MobileMenu from './Navigation/MobileMenu';
import UserMobileMenu from './Navigation/UserMobileMenu';
import UserProfileModal from './Navigation/UserProfileModal';
import IndicationsModal from './Navigation/IndicationsModal';
import { useLayoutModals } from './useLayoutModals';

const PrivateLayout = ({ children }: PropsWithRequiredChildren) => {
  const isFloatingPlayerVisible = useFloatingPlayer(
    selectIsFloatingPlayerVisible,
  );
  const shouldShowDesktopSidebar = useBreakpoint(up('lg'));

  const {
    isSurveyEnabled,
    isSurveyModalVisible,
    toggleIsSurveyModalVisible,
    isSurveyNoticeVisible,
    closeSurveyNotice,
    isCertificateModalVisible,
    toggleIsCertificateModalVisible,
    isMobileMenuVisible,
    toggleIsMobileMenuVisible,
    isUserMobileMenuVisible,
    toggleIsUserMobileMenuVisible,
    isProfileModalVisible,
    toggleIsProfileModalVisible,
    isIndicationsModalVisible,
    toggleIsIndicationsModalVisible,
  } = useLayoutModals();

  useSurveyChannel();
  useTimelineChannel();
  useNotifyUserIsOnlineEffect();

  const shouldDisplaySurveyModal = isSurveyEnabled && isSurveyModalVisible;

  return (
    <>
      <AppShell
        header={
          <Header
            onMobileMenuClick={
              shouldShowDesktopSidebar ? undefined : toggleIsMobileMenuVisible
            }
            onIndicationsClick={toggleIsIndicationsModalVisible}
            languageSelector={
              shouldShowDesktopSidebar ? <DropdownLang /> : null
            }
            userDropdown={
              shouldShowDesktopSidebar ? (
                <DropdownMenu
                  toggleProfileModal={toggleIsProfileModalVisible}
                />
              ) : undefined
            }
            onUserClick={
              shouldShowDesktopSidebar
                ? undefined
                : toggleIsUserMobileMenuVisible
            }
          />
        }
        sidebar={
          shouldShowDesktopSidebar ? (
            <OldSidebar
              handleSurvey={toggleIsSurveyModalVisible}
              handleCertificate={toggleIsCertificateModalVisible}
            />
          ) : null
        }
      >
        <AnimatePresence>
          {isSurveyNoticeVisible && (
            <SurveyNotification
              close={closeSurveyNotice}
              handleSurvey={toggleIsSurveyModalVisible}
            />
          )}
        </AnimatePresence>

        {children}
      </AppShell>

      <TawkTo location={IntegrationLocation.AUTH_LAYOUT} />
      <WhatsAppButton location={IntegrationLocation.AUTH_LAYOUT} />

      {/* Mobile Menus */}
      <AnimatePresence>
        {isMobileMenuVisible && (
          <Modal handleClose={toggleIsMobileMenuVisible}>
            <MobileMenu
              handleSurvey={toggleIsSurveyModalVisible}
              handleCertificate={toggleIsCertificateModalVisible}
              handleClose={toggleIsMobileMenuVisible}
            />
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isUserMobileMenuVisible && (
          <Modal handleClose={toggleIsUserMobileMenuVisible}>
            <UserMobileMenu
              handleProfileModal={toggleIsProfileModalVisible}
              handleClose={toggleIsUserMobileMenuVisible}
            />
          </Modal>
        )}
      </AnimatePresence>

      {/* Profile Modal */}
      <AnimatePresence>
        {isProfileModalVisible && (
          <Modal handleClose={toggleIsProfileModalVisible}>
            <UserProfileModal handleClose={toggleIsProfileModalVisible} />
          </Modal>
        )}
      </AnimatePresence>

      {/* Survey Modal */}
      <AnimatePresence>
        {shouldDisplaySurveyModal && (
          <Modal handleClose={toggleIsSurveyModalVisible}>
            <SurveyModal
              handleClose={toggleIsSurveyModalVisible}
              handleCertificate={toggleIsSurveyModalVisible}
            />
          </Modal>
        )}
      </AnimatePresence>

      {/* Certificate Modal */}
      <AnimatePresence>
        {isCertificateModalVisible && (
          <Modal handleClose={toggleIsCertificateModalVisible}>
            <CertificateModal />
          </Modal>
        )}
      </AnimatePresence>

      {/* Indications Modal */}
      <AnimatePresence>
        {isIndicationsModalVisible && (
          <Modal handleClose={toggleIsIndicationsModalVisible}>
            <IndicationsModal handleClose={toggleIsIndicationsModalVisible} />
          </Modal>
        )}
      </AnimatePresence>

      {/* Floating Player */}
      <AnimatePresence>
        {isFloatingPlayerVisible && <FloatingPlayer />}
      </AnimatePresence>

      <GlobalModals />
    </>
  );
};

export default PrivateLayout;
